import { useState } from 'react'

import { useSesion } from '@/modules/auth/useSesion'
import { Boton } from '@/shared/ui/Boton/Boton'

import { celularLegible, useCambiarWhatsAppDeEnvio, useWhatsAppDeEnvio } from './useWhatsAppDeEnvio'
import estilos from './WhatsAppDeEnvio.module.css'

/**
 * A QUÉ WHATSAPP SALEN LA COTIZACIÓN Y EL ACTA (0273).
 *
 * Johan: «no siempre son los del RUT, como una casilla para yo poner el
 * número al que quiero que salga». Muestra el que se va a usar —el de la
 * orden si se puso, si no el del cliente— y deja cambiarlo solo para esta
 * orden. Va en la ficha y en el momento de enviar la cotización, para que
 * nadie mande sin ver a dónde.
 */
export function WhatsAppDeEnvio({
  ordenServicioId,
  cotizacionId,
  compacto = false,
  sinRotulo = false,
}: {
  ordenServicioId?: string
  cotizacionId?: string
  /** En las pantallas de envío: en su propia caja. */
  compacto?: boolean
  /** En la ficha el rótulo lo pone la fila, como a los demás datos. */
  sinRotulo?: boolean
}) {
  const { puede } = useSesion()
  const { data: envio } = useWhatsAppDeEnvio({ ordenServicioId, cotizacionId })
  const cambiar = useCambiarWhatsAppDeEnvio()
  const [editando, setEditando] = useState(false)
  const [numero, setNumero] = useState('')

  if (!envio) return null
  const puedeCambiar = puede('ordenes_servicio', 'editar')

  function guardar(valor: string) {
    if (!envio) return
    cambiar.mutate(
      { ordenServicioId: envio.orden_servicio_id, numero: valor },
      { onSuccess: () => setEditando(false) },
    )
  }

  return (
    <div className={`${estilos.caja} ${compacto ? estilos.compacto : ''}`}>
      {!sinRotulo && <p className={estilos.rotulo}>WhatsApp para cotización y acta</p>}

      {!editando ? (
        <div className={estilos.linea}>
          {envio.whatsapp ? (
            <>
              <span className={estilos.numero}>{celularLegible(envio.whatsapp)}</span>
              <span className={estilos.origen}>
                {envio.origen === 'orden' ? 'solo para esta orden' : 'el del cliente'}
              </span>
            </>
          ) : (
            <span className={estilos.falta}>
              Sin WhatsApp: ni la cotización ni el acta le llegarían.
            </span>
          )}
          {puedeCambiar && (
            <button
              type="button"
              className={estilos.cambiar}
              onClick={() => {
                setNumero(envio.origen === 'orden' ? celularLegible(envio.whatsapp) : '')
                cambiar.reset()
                setEditando(true)
              }}
            >
              {envio.whatsapp ? 'Cambiar' : 'Poner número'}
            </button>
          )}
        </div>
      ) : (
        <div className={estilos.edicion}>
          <input
            className={estilos.campo}
            type="tel"
            inputMode="numeric"
            autoComplete="off"
            placeholder="Celular, ej. 300 123 4567"
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            aria-label="WhatsApp para la cotización y el acta de esta orden"
            autoFocus
          />
          <Boton
            variante="principal"
            tamano="pequeno"
            onClick={() => guardar(numero)}
            trabajando={cambiar.isPending}
            disabled={numero.replace(/\D/g, '').length < 10}
          >
            Guardar
          </Boton>
          <Boton variante="secundario" tamano="pequeno" onClick={() => setEditando(false)}>
            Cancelar
          </Boton>
          {envio.origen === 'orden' && (
            <button type="button" className={estilos.cambiar} onClick={() => guardar('')}>
              Volver al del cliente
              {envio.del_cliente ? ` (${celularLegible(envio.del_cliente)})` : ''}
            </button>
          )}
          <p className={estilos.nota}>
            Solo para esta orden: el del cliente no cambia. Queda en la bitácora.
          </p>
          {cambiar.error && <p className={estilos.error}>{cambiar.error.message}</p>}
        </div>
      )}
    </div>
  )
}
